import { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from 'api';

/**
 * Fills a signed-in customer's saved allergies/restrictions into the primary
 * ("Me") household member — only when that member has none recorded yet, so
 * a completed quiz or a manual edit is never clobbered. Guest shoppers see no
 * change.
 *
 * `householdMembers`/`updateHouseholdMember` come from FlowContext (see
 * useFlow()). Kept as its own hook rather than living in FlowProvider so the
 * fetch only fires on pages that actually show household members, not on
 * every storefront page FlowProvider wraps — same reasoning as
 * useDeliveryPrefill.
 */
export function useRestrictionsPrefill(householdMembers, updateHouseholdMember) {
  const { isAuthenticated, isLoading } = useAuth0();
  const ranRef = useRef(false);

  useEffect(() => {
    // householdMembers/updateHouseholdMember are intentionally not deps — this
    // should only run once per sign-in, not re-fire as the member list changes.
    if (isLoading || !isAuthenticated || ranRef.current) return;
    const primary = householdMembers?.[0];
    if (!primary || (primary.restrictionTags || []).length) return;
    ranRef.current = true;

    let cancelled = false;
    api.get('/account/restrictions', { timeout: 10000 })
      .then(({ data }) => {
        if (cancelled || !data?.success || !data.restrictionTags?.length) return;
        updateHouseholdMember(primary.id, {
          restrictionTags: data.restrictionTags,
          restrictionLabels: data.restrictionLabels || {},
        });
      })
      // A failed prefill leaves the member with no allergies recorded, same as
      // a guest — this is a convenience, not something ordering depends on.
      .catch(() => {});

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading, householdMembers?.[0]?.email]);
}
